import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { SightingTypeBadge, SightingStatusBadge, SightingSourceBadge } from "./SightingBadges";
import type { Sighting } from "../types";
import { formatDistanceToNow } from "date-fns";

interface SightingsTableProps {
  sightings: Sighting[];
  orderBy: string;
  order: "asc" | "desc";
  onSort: (property: string) => void;
}

const columns = [
  { id: "id", label: "W/O", width: 120 },
  { id: "referenceNumber", label: "Ref", width: 140 },
  { id: "type", label: "Type", width: 130 },
  { id: "status", label: "Status", width: 120 },
  { id: "source", label: "Source", width: 110 },
  { id: "location", label: "Location", width: "auto" },
  { id: "zone", label: "Zone", width: 70 },
  { id: "dateReported", label: "Reported", width: 120 },
  { id: "actions", label: "", width: 50 },
];

export const SightingsTable = ({ sightings, orderBy, order, onSort }: SightingsTableProps) => {
  const navigate = useNavigate();

  const handleRowClick = (sightingId: string) => {
    navigate(`/sightings/${sightingId}`);
  };

  if (sightings.length === 0) {
    return (
      <Card variant="outlined" sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No sightings found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Try adjusting your filters to see more results
        </Typography>
      </Card>
    );
  }

  return (
    <TableContainer component={Card} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id} sx={{ width: column.width }}>
                {column.id !== "actions" ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => onSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : null}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sightings.map((sighting) => (
            <TableRow
              key={sighting.id}
              hover
              onClick={() => handleRowClick(sighting.id)}
              sx={{ cursor: "pointer" }}
            >
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  {sighting.id}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                  {sighting.referenceNumber}
                </Typography>
              </TableCell>
              <TableCell>
                <SightingTypeBadge type={sighting.type} />
              </TableCell>
              <TableCell>
                <SightingStatusBadge status={sighting.status} />
              </TableCell>
              <TableCell>
                <SightingSourceBadge source={sighting.source} />
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {sighting.location}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Chip label={sighting.zone} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatDistanceToNow(new Date(sighting.dateReported), { addSuffix: true })}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(sighting.id);
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
